import React, { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import {
  useTheme,
  useMediaQuery,
  ButtonGroup,
  Button,
  IconButton,
  MenuItem,
  TextField
} from '@mui/material'
import {
  PlaylistAdd
} from '@mui/icons-material'

// Third-party Imports
import { rankItem } from '@tanstack/match-sorter-utils'
import { toast } from 'react-toastify'

// Component Imports
import CustomTextField from '../atoms/CustomTextField'
import DebouncedInput from '../atoms/DebouncedInput'
import AdditionalEmailModal from '../molecules/AdditionalEmailModal'
import UploadExcelFirmListModalBtn from '../molecules/UploadExcelFirmListModalBtn'
import FirmListClientSide from './FirmListClientSide'
import FirmListServerSide from './FirmListServerSide'
import AutoCompleteChip from '../atoms/AutoCompleteChip'

// Helpers
import McPageHelpers from '../../../../../pages/mc-components/McPageHelpers'
import { getUrlParamsObj } from '../../../services/urlServices'

import { useGlobalStoreData } from '../../../../../store/globalStore'

// API
import { getRegistryFirms } from '../../../services/B2BServices'

const fuzzyFilter = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value)

  // Store the itemRank info
  addMeta({
    itemRank
  })

  // Return if the item should be filtered in/out
  return itemRank.passed
}

let apiController

const FirmListManager = (props) => {
  const {
    filteredRegistryFirmsData,
    setFilteredRegistryFirmsData,
    serverSidePagination,
    setServerSidePagination,
    filteredSectors,
    setFilteredSectorsByKey,
    selectedTradeRegistrySectors,
    setSelectedTradeRegistrySectors,
    tradeRegistrySectors,
    firmKeys,
    activeFirmKey,
    setActiveFirmKey,
    data,
    rowSelection,
    setRowSelection,
    setSelectedFirms,
    fetching,
    additionalEmails,
    setAdditionalEmails,
    excelFirms,
    setExcelFirms,
    isServerSide
  } = props
  const { t } = useTranslation()
  const theme = useTheme()

  const languageGSD = useGlobalStoreData.language()

  const queryParams = getUrlParamsObj()
  const isBelowMdScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isBelowSmScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [globalFilter, setGlobalFilter] = useState('')
  const [additionalEmailsModalOpen, setAdditionalEmailsModalOpen] = useState(false)
  const [pageSize, setPageSize] = useState(10)
  const [registryFirmsFetching, setRegistryFirmsFetching] = useState(false)

  const filteredSectorsByKey = filteredSectors?.[activeFirmKey] || []

  const setFilteredRegistryFirmsDataByKey = (key, val) => {
    setFilteredRegistryFirmsData((prevState) => ({ ...prevState, [key]: val }))
  }

  const setServerSidePaginationByKey = (key, val) => {
    setServerSidePagination((prevState) => ({ ...prevState, [key]: val }))
  }

  const handleSelectAllSectors = (allSelected) => {
    if (allSelected) {
      setSelectedTradeRegistrySectors(filteredSectorsByKey)
    } else {
      setSelectedTradeRegistrySectors([])
    }
  }

  useEffect(() => {
    getRegistryFirmsByPagination(
      filteredSectorsByKey.map((item) => item.code),
      serverSidePagination?.[activeFirmKey]?.pageIndex || 0,
      serverSidePagination?.[activeFirmKey]?.pageSize || pageSize
    )
  }, [serverSidePagination, filteredSectors])

  const getRegistryFirmsByPagination = (sectorCodes, pageIndex, pageSize) => {
    apiController && apiController.abort()

    apiController = new AbortController()

    if (sectorCodes.length) {
      setRegistryFirmsFetching(true)
      getRegistryFirms(
        apiController,
        {
          b2bCampaignId: queryParams.b2bCampaignId,
          sectorCodes: sectorCodes.join(';'),
          page: pageIndex,
          pageSize,
          sectorCategory: activeFirmKey
        }).then((res) => {
        if (res.status === 200) {
          setFilteredRegistryFirmsDataByKey(activeFirmKey, res.data)
        } else {
          toast.error(t('mailTemplate.wizard.firmList.tradeRegistryFetchErr'), {
            position: 'top-right',
            autoClose: true,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: false
          })
        }
        setRegistryFirmsFetching(false)
      }).catch((err) => {
        if (!err?.config?.signal?.aborted) {
          setRegistryFirmsFetching(false)
          toast.error(t('mailTemplate.wizard.firmList.tradeRegistryFetchErr'), {
            position: 'top-right',
            autoClose: true,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: false
          })
        }
      })
    } else {
      setFilteredRegistryFirmsDataByKey(activeFirmKey, [])
      setRegistryFirmsFetching(false)
    }
  }

  return (
    <>
      <div className='flex flex-wrap justify-between items-center p-2 gap-2'>
        <div className='flex items-center gap-2 grow' style={{
          justifyContent: isBelowSmScreen ? 'space-between' : 'start'
        }}>
          <div className='flex flex-col'>
            <IconButton
              onClick={() => { setAdditionalEmailsModalOpen(true) }}
              color='primary'
            >
              <PlaylistAdd/>
            </IconButton>

            <UploadExcelFirmListModalBtn
              excelFirms={excelFirms}
              setExcelFirms={setExcelFirms}
            />
          </div>
          <ButtonGroup
            size={isBelowMdScreen ? 'small' : 'medium'}
            orientation={isBelowSmScreen ? 'vertical' : 'horizontal'}
            fullWidth={isBelowSmScreen}
            variant="outlined"
            aria-label="Basic button group"
            className='dir-rtl justify-start'
            style={{
              maxWidth: '100%'
            }}
            disabled={registryFirmsFetching}
          >
            {firmKeys?.map((item, index) => {
              const localizationPath = `mailTemplate.wizard.firmList.tables.${item}`
              const localizedText = t(localizationPath)

              const btnText = localizationPath !== localizedText ? localizedText : item

              return (
                <Button
                  variant={activeFirmKey === item ? 'contained' : 'outlined'}
                  key={index}
                  onClick={() => setActiveFirmKey(item)}
                >{btnText}</Button>
              )
            })}
          </ButtonGroup>
        </div>
        <div className='flex flex-grow justify-end gap-2'>
          <div style={{ minWidth: 200, maxWidth: isBelowSmScreen ? 'unset' : 300, width: '100%' }}>
            {isServerSide
              ? <AutoCompleteChip
                  size='small'
                  value={filteredSectorsByKey}
                  onChange={(e, val) => {
                    setFilteredSectorsByKey(val)
                    setSelectedTradeRegistrySectors([])
                    setServerSidePaginationByKey(activeFirmKey, { pageSize: serverSidePagination?.[activeFirmKey]?.pageSize || 10, pageIndex: 0 })
                  }}
                  options={tradeRegistrySectors || []}
                  getOptionLabel={(opt) => opt.code + (opt?.name?.[languageGSD] ? `-${opt.name[languageGSD]}` : '')}
                  chipLabelKey={'code'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('mailTemplate.wizard.firmList.sector')}
                      placeholder={t('mailTemplate.wizard.firmList.search')}
                      error={!filteredSectorsByKey?.length}
                    />
                  )}
                />
              : <DebouncedInput
                  fullWidth
                  value={globalFilter ?? ''}
                  onChange={value => setGlobalFilter(String(value))}
                  placeholder={t('mailTemplate.wizard.firmList.search')}
                  className='is-full sm:is-auto'
                />
            }
          </div>

          <CustomTextField
            select
            value={isServerSide ? serverSidePagination?.[activeFirmKey]?.pageSize || 10 : pageSize.toString()}
            onChange={e => {
              const val = Number(e.target.value)

              if (isServerSide) {
                setServerSidePaginationByKey(activeFirmKey, { pageIndex: 0, pageSize: val })
              } else {
                setPageSize(val)
              }
            }}
            className='is-[70px]'
            disabled={registryFirmsFetching}
          >
            <MenuItem value='10'>{McPageHelpers.numToLocaleString(10)}</MenuItem>
            <MenuItem value='25'>{McPageHelpers.numToLocaleString(25)}</MenuItem>
            <MenuItem value='50'>{McPageHelpers.numToLocaleString(50)}</MenuItem>
          </CustomTextField>
        </div>
      </div>

      {isServerSide
        ? <FirmListServerSide
            serverSidePagination={serverSidePagination[activeFirmKey]}
            handleSelectAllSectors={handleSelectAllSectors}
            selectedSectors={selectedTradeRegistrySectors}
            setServerSidePaginationByKey={(val) =>
              setServerSidePaginationByKey(activeFirmKey, val)
            }
            data={filteredRegistryFirmsData?.[activeFirmKey]?.registryFirms || []}
            rowCount={filteredRegistryFirmsData?.[activeFirmKey]?.totalRecordCount || 0}
            fetching={registryFirmsFetching}
            languageGSD={languageGSD}
          />
        : <FirmListClientSide
            data={data}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            setSelectedFirms={setSelectedFirms}
            fetching={fetching}
            fuzzyFilter={fuzzyFilter}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            pageSize={pageSize}
          />
      }
      <AdditionalEmailModal
        open={additionalEmailsModalOpen}
        setOpen={setAdditionalEmailsModalOpen}
        additionalEmails={additionalEmails}
        setAdditionalEmails={setAdditionalEmails}
      />
    </>
  )
}

export default FirmListManager
